import type { ReactNode } from 'react'

import type {
  TaskFeedSort,
  TaskListTypeFilter
} from '@/features/tasks/api/watchTaskApi'

import type { TaskFeedPlatform } from '../TaskFeedCard/taskFeedCard.constants'

export type TasksFeedChip<C extends string> = {
  id: C
  label: string
  icon?: ReactNode
  /** A counter badge next to the label; hidden when 0 or absent. */
  count?: number
  /** Not available yet: shown disabled with a «Soon» badge. */
  soon?: boolean
}

export type TasksFeedSortId =
  | 'default'
  | 'rewardDesc'
  | 'rewardAsc'
  | 'deadlineAsc'
  | 'newest'
  | 'spotsLeftAsc'

export const EXPLORE_FEED_SORTS: readonly TasksFeedSortId[] = [
  'default',
  'rewardDesc',
  'rewardAsc',
  'deadlineAsc',
  'newest',
  'spotsLeftAsc'
]

export const MY_FEED_SORTS: readonly TasksFeedSortId[] = [
  'default',
  'rewardDesc',
  'rewardAsc',
  'deadlineAsc'
]

export const TASKS_FEED_SORT_LABEL_KEY = {
  default: 'sortDefault',
  rewardDesc: 'sortRewardDesc',
  rewardAsc: 'sortRewardAsc',
  deadlineAsc: 'sortDeadlineAsc',
  newest: 'sortNewest',
  spotsLeftAsc: 'sortSpotsLeftAsc'
} as const satisfies Record<TasksFeedSortId, string>

const SORT_PARAM: Record<TasksFeedSortId, TaskFeedSort | undefined> = {
  default: undefined,
  rewardDesc: 'reward_desc',
  rewardAsc: 'reward_asc',
  deadlineAsc: 'deadline_asc',
  newest: 'newest',
  spotsLeftAsc: 'spots_left_asc'
}

/** The server `sort` param for a feed order; none keeps the default order. */
export function toFeedSortParam(
  sort: TasksFeedSortId
): TaskFeedSort | undefined {
  return SORT_PARAM[sort]
}

/** Types My tasks lists in work; watch progress lives on the watch page. */
export const MY_FEED_TYPES: readonly TaskListTypeFilter[] = [
  'share',
  'action',
  'create'
]

/** Types a fan cannot take yet: their chip says «Soon». */
export const SOON_FEED_TYPES: readonly TaskListTypeFilter[] = ['create']

/** The Task type menu of My tasks: ended watch tasks are listed too. */
const MY_FEED_ALL_TYPES: readonly TaskListTypeFilter[] = [
  'watch',
  ...MY_FEED_TYPES
]

export const MY_FEED_TYPE_OPTIONS: readonly TaskListTypeFilter[] =
  MY_FEED_ALL_TYPES.filter(type => !SOON_FEED_TYPES.includes(type))

/** The Platforms menu groups: social networks, then the stores. */
export const FEED_PLATFORM_GROUPS = [
  {
    labelKey: 'platformsSocial',
    platforms: ['instagram', 'tiktok', 'x', 'youtube', 'facebook']
  },
  { labelKey: 'platformsStores', platforms: ['app_store', 'google_play'] }
] as const satisfies readonly {
  labelKey: string
  platforms: readonly TaskFeedPlatform[]
}[]
